import React from 'react';
import styled, { useTheme } from 'styled-components';

interface BotaoProps {
  cor: string;
}

const Container = styled.div`
  display: flex;
  align-self: center;
  flex-direction: row;
  justify-content: center;
  gap: 15px;
  margin-bottom: 0;
  margin-left: 0;
  width: auto;
`;

const WorkspaceBotao = styled.button<BotaoProps>`
  border-radius: 10px;
  width: 9vw;
  border: 1px solid ${({ cor }) => cor};
  padding: 8px;
  font: normal normal normal 1rem/1.25rem Rawline;
  background-color: ${({ cor }) => cor};
  color: white;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 4px;
  transition: all 0.3s ease;

  span {
    font-size: 1.2rem;
  }

  &:hover {
    transform: scale(1.05);
  }

  &:disabled {
    background-color: ${({ cor }) => cor};
    opacity: 0.6;
    cursor: not-allowed;
    filter: grayscale(50%); /* Adicionar efeito acinzentado */
  }
`;

interface WorkspaceBotoesProps {
  delete?: boolean;
  deleteDisabled?: boolean;
  onClickDelete?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  pdf?: boolean;
  pdfDisabled?: boolean;
  onClickPdf?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  download?: boolean;
  downloadDisabled?: boolean;
  onClickDownload?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  save?: boolean;
  saveDisabled?: boolean;
  onClickSave?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  edit?: boolean;
  editDisabled?: boolean;
  onClickEdit?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  senha?: boolean;
  senhaDisabled?: boolean;
  onClickSenha?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  cancelar?: boolean;
  cancelarDisabled?: boolean;
  onClickCancelar?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  inserir?: boolean;
  inserirDisabled?: boolean;
  onClickInserir?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  open?: boolean;
  openDisabled?: boolean;
  onClickOpen?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  limparTags?: boolean;
  limparTagsDisabled?: boolean;
  onClickLimparTags?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  remover?: boolean;
  removerDisabled?: boolean;
  onClickRemover?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  relatorio?: boolean;
  relatorioDisabled?: boolean;
  onClickRelatorio?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  upload?: boolean;
  selecionar?: boolean;
  onClickSelecionar?: React.MouseEventHandler<HTMLButtonElement> | undefined;
}

// Alinhamento padrão do componente WorkspaceBotoes é center.
// Para alterá-lo deve ser alinhado em uma div no componente pai.
const WorkspaceBotoes: React.FC<WorkspaceBotoesProps> = ({
  delete: showDelete,
  deleteDisabled,
  onClickDelete,
  pdf: showPdf,
  pdfDisabled,
  onClickPdf,
  download: showDownload,
  downloadDisabled,
  onClickDownload,
  save: showSave,
  saveDisabled,
  onClickSave,
  edit: showEdit,
  onClickEdit,
  senha: showSenha,
  senhaDisabled,
  onClickSenha,
  cancelar: showCancelar,
  cancelarDisabled,
  onClickCancelar,
  inserir: showInserir,
  inserirDisabled,
  onClickInserir,
  open: showOpen,
  openDisabled,
  onClickOpen,
  limparTags: showLimparTags,
  limparTagsDisabled,
  onClickLimparTags,
  remover: showRemover,
  removerDisabled,
  onClickRemover,
  relatorio: showRelatorio,
  relatorioDisabled,
  onClickRelatorio,
  upload: showUpload,
  selecionar: showSelecionar,
  onClickSelecionar,
}) => {
  const theme = useTheme();

  return (
    <Container>
      {showLimparTags && (
        <WorkspaceBotao
          onClick={onClickLimparTags}
          cor={theme.colors.amarelogov}
          disabled={limparTagsDisabled}
        >
          Limpar Tags
        </WorkspaceBotao>
      )}

      {showRemover && (
        <WorkspaceBotao
          onClick={onClickRemover}
          cor={theme.colors.danger}
          disabled={removerDisabled}
        >
          Remover
        </WorkspaceBotao>
      )}

      {showCancelar && (
        <WorkspaceBotao
          onClick={onClickCancelar}
          cor={theme.colors.botaoLaranja}
          disabled={cancelarDisabled}
        >
          Cancelar
        </WorkspaceBotao>
      )}
      {showDelete && (
        <WorkspaceBotao
          onClick={onClickDelete}
          cor={theme.colors.workspace.botaoVermelho}
          disabled={deleteDisabled}
        >
          Deletar
        </WorkspaceBotao>
      )}

      {showPdf && (
        <WorkspaceBotao
          onClick={onClickPdf}
          cor={theme.colors.verdegov}
          disabled={pdfDisabled}
        >
          Exportar PDF
        </WorkspaceBotao>
      )}

      {showDownload && (
        <WorkspaceBotao
          onClick={onClickDownload}
          cor={theme.colors.amarelogov}
          style={{ color: 'black' }}
          disabled={downloadDisabled}
        >
          Exportar Dados
        </WorkspaceBotao>
      )}

      {showSenha && (
        <WorkspaceBotao
          onClick={onClickSenha}
          cor={theme.colors.roxo}
          disabled={senhaDisabled}
        >
          Alterar Senha
        </WorkspaceBotao>
      )}

      {showSave && (
        <WorkspaceBotao
          onClick={onClickSave}
          cor={theme.colors.azulgov}
          disabled={saveDisabled}
        >
          Salvar
        </WorkspaceBotao>
      )}

      {showEdit && (
        <WorkspaceBotao
          onClick={onClickEdit}
          cor={theme.colors.azulgov}
          disabled={saveDisabled}
        >
          Editar
        </WorkspaceBotao>
      )}

      {showInserir && (
        <WorkspaceBotao
          onClick={onClickInserir}
          cor={theme.colors.padraoAzulEscuro}
          disabled={inserirDisabled}
        >
          Inserir
        </WorkspaceBotao>
      )}

      {showSelecionar && (
        <WorkspaceBotao
          onClick={onClickSelecionar}
          cor={theme.colors.padraoAzulEscuro}
        >
          Selecionar
        </WorkspaceBotao>
      )}

      {showOpen && (
        <WorkspaceBotao
          onClick={onClickOpen}
          cor={theme.colors.padraoAzulEscuro}
          disabled={openDisabled}
        >
          Abrir
        </WorkspaceBotao>
      )}

      {showRelatorio && (
        <WorkspaceBotao
          onClick={onClickRelatorio}
          cor={theme.colors.success}
          disabled={relatorioDisabled}
        >
          Relatorio
        </WorkspaceBotao>
      )}

      {showUpload && (
        <WorkspaceBotao
          onClick={onClickRelatorio}
          cor={theme.colors.amarelogov}
          disabled={relatorioDisabled}
        >
          Modelo Arquivo
        </WorkspaceBotao>
      )}

      {showUpload && (
        <WorkspaceBotao
          onClick={onClickRelatorio}
          cor={theme.colors.success}
          disabled={relatorioDisabled}
        >
          Upload
        </WorkspaceBotao>
      )}
    </Container>
  );
};

export default WorkspaceBotoes;
